import { useState } from 'react';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, XCircle, AlertTriangle, Loader2, RefreshCw, HelpCircle } from 'lucide-react';

const criteriaLabels = {
  story_format: { name: 'Story Format', desc: 'As a / I want / So that' },
  acceptance_criteria: { name: 'Acceptance Criteria', desc: '3-6 testable statements' },
  dependencies: { name: 'Dependencies', desc: 'Blockers identified' },
  invest: { name: 'INVEST', desc: 'Independent, Negotiable, Valuable, Estimable, Small, Testable' },
  pointed: { name: 'Story Pointed', desc: 'Fibonacci story points assigned' },
  no_open_questions: { name: 'No Open Questions', desc: 'All ambiguities resolved' },
};

const statusIcon = {
  pass: <CheckCircle size={16} className="text-green-500" />,
  fail: <XCircle size={16} className="text-red-500" />,
  warning: <AlertTriangle size={16} className="text-amber-500" />,
  unchecked: <HelpCircle size={16} className="text-gray-400" />,
};

const statusBadge = {
  pass: 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400',
  fail: 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400',
  warning: 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400',
  unchecked: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
};

export default function StoryReadinessPanel({ storyId, readiness, onUpdate }) {
  const [evaluating, setEvaluating] = useState(false);

  const evaluate = async () => {
    setEvaluating(true);
    try {
      const res = await api.post(`/sprints/stories/${storyId}/evaluate-readiness`);
      onUpdate?.(res.data);
    } catch (err) {
      console.error('Evaluation failed:', err);
    } finally {
      setEvaluating(false);
    }
  };

  const override = async (criterion, status) => {
    try {
      const res = await api.patch(`/sprints/stories/${storyId}/readiness/${criterion}`, {
        status,
        details: status === 'pass' ? 'Manually confirmed by PM' : 'Manually marked as failing'
      });
      onUpdate?.(res.data);
    } catch (err) {
      console.error('Override failed:', err);
    }
  };

  const checks = readiness?.checks || [];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-bold text-[#30302F] dark:text-white">Dev Readiness</h4>
          {readiness && (
            <Badge className={`text-[11px] rounded-none ${
              readiness.ready_for_dev ? 'bg-green-100 text-green-700' :
              readiness.overall_status === 'partial' ? 'bg-amber-100 text-amber-700' :
              'bg-red-100 text-red-700'
            }`}>
              {readiness.score_summary || 'Not evaluated'}
            </Badge>
          )}
        </div>
        <button
          onClick={evaluate}
          disabled={evaluating}
          className="text-xs flex items-center gap-1 text-[#98139C] hover:text-[#7a0f7d] disabled:opacity-50"
        >
          {evaluating ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          {evaluating ? 'Evaluating...' : 'Evaluate'}
        </button>
      </div>

      {checks.length > 0 ? (
        <div className="space-y-2">
          {Object.entries(criteriaLabels).map(([key, label]) => {
            const check = checks.find(c => c.criterion === key);
            const status = check?.status || 'unchecked';
            return (
              <div key={key} className="flex items-start gap-2 p-2 bg-gray-50 dark:bg-white/5 rounded">
                {statusIcon[status]}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-[#30302F] dark:text-white">{label.name}</span>
                    <Badge className={`text-[10px] rounded-none ${statusBadge[status]}`}>{status}</Badge>
                    {check?.evaluated_by === 'ai' && (
                      <span className="text-[10px] text-gray-400">AI &middot; {Math.round((check.score || 0) * 100)}%</span>
                    )}
                  </div>
                  {check?.details && (
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">{check.details}</p>
                  )}
                  {(key === 'no_open_questions' || check?.evaluated_by === 'manual') && status !== 'pass' && (
                    <button
                      onClick={() => override(key, 'pass')}
                      className="text-[11px] text-[#98139C] hover:underline mt-1"
                    >
                      Mark as Pass
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-xs text-gray-400">Click "Evaluate" to run readiness checks</p>
      )}
    </div>
  );
}

export function ReadinessBadge({ readiness }) {
  if (!readiness) {
    return <Badge className="text-[10px] rounded-none bg-gray-100 text-gray-500 dark:bg-gray-800">&mdash;</Badge>;
  }
  const { ready_for_dev, score_summary, overall_status } = readiness;
  const color = ready_for_dev
    ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400'
    : overall_status === 'partial'
    ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400'
    : 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400';

  return <Badge className={`text-[10px] rounded-none ${color}`}>{score_summary}</Badge>;
}
